/**
 * Radio Component
 *
 * Accessible radio button for single-choice selections.
 * Custom styled with proper ARIA attributes and keyboard support.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { forwardRef, useId, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { cn } from '../../../lib/utils';

export interface RadioProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
  label?: string;
  helperText?: string;
  error?: string;
}

export interface RadioGroupProps extends ComponentPropsWithoutRef<'div'> {
  label?: string;
  error?: string;
  helperText?: string;
  name: string;
  children: React.ReactNode;
}

/**
 * Radio Component
 *
 * @example
 * ```tsx
 * <Radio
 *   label="Option A"
 *   name="choice"
 *   value="a"
 *   checked={selected === 'a'}
 *   onChange={(e) => setSelected(e.target.value)}
 * />
 * ```
 */
const Radio = forwardRef<ElementRef<'input'>, RadioProps>(
  ({ className, label, helperText, error, disabled, id, ...props }, ref) => {
    const autoId = useId();
    const radioId = id || autoId;

    return (
      <div className="flex items-start gap-3">
        <div className="relative flex items-center">
          {/* Hidden native radio for functionality */}
          <input
            ref={ref}
            type="radio"
            id={radioId}
            disabled={disabled}
            className={cn(
              // Hide native radio but keep it accessible
              'peer sr-only',
              className
            )}
            {...props}
          />
          {/* Custom radio indicator - direct sibling of peer input */}
          <span
            className={cn(
              // Radio circle
              'relative flex h-5 w-5 items-center justify-center rounded-full border-2',
              'transition-colors duration-200',

              // Default state
              'border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900',

              // Checked state - WCAG AA contrast
              'peer-checked:border-primary-600 peer-checked:bg-primary-600',

              // Inner dot via before pseudo-element
              'before:content-[""] before:block before:w-2 before:h-2 before:rounded-full before:bg-white',
              'before:opacity-0 before:transition-opacity before:duration-200',
              'peer-checked:before:opacity-100',

              // Focus state
              'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2',

              // Error state
              error && 'border-red-500'
            )}
          />
          {/* BFSG 44x44px touch target overlay */}
          <label
            htmlFor={radioId}
            className={cn(
              'absolute inset-0 cursor-pointer',
              'min-h-[44px] min-w-[44px]',
              '-m-2.5',
              disabled && 'cursor-not-allowed'
            )}
            aria-hidden="true"
          />
        </div>

        {/* Label and helper text */}
        {(label || helperText || error) && (
          <div className="flex flex-1 flex-col gap-1">
            {label && (
              <label
                htmlFor={radioId}
                className={cn(
                  'cursor-pointer text-sm font-medium text-neutral-900 dark:text-neutral-100',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              >
                {label}
              </label>
            )}
            {error && (
              <span className="text-sm text-red-600" role="alert">
                {error}
              </span>
            )}
            {helperText && !error && (
              <span className="text-sm text-neutral-600 dark:text-neutral-400">{helperText}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Radio.displayName = 'Radio';

/**
 * RadioGroup Component - Wrapper for grouped radios
 *
 * @example
 * ```tsx
 * <RadioGroup label="Choose an option" name="choice">
 *   <Radio label="Option A" value="a" />
 *   <Radio label="Option B" value="b" />
 * </RadioGroup>
 * ```
 */
const RadioGroup = forwardRef<ElementRef<'div'>, RadioGroupProps>(
  ({ className, label, error, helperText, name: _name, children, ...props }, ref) => {
    const autoGroupId = useId();
    const groupId = autoGroupId;

    return (
      <div ref={ref} role="radiogroup" aria-labelledby={groupId} className={className} {...props}>
        {label && (
          <label id={groupId} className="mb-3 block text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {label}
          </label>
        )}
        <div className="space-y-3">{children}</div>
        {error && (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">{helperText}</p>
        )}
      </div>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

export { Radio, RadioGroup };
